'use client'

import { getIntegrationConfig } from '@/lib/integrations'
import { Copy } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { WorkflowVersionUtils } from 'sprites-types/api/v2/workflows'
import { WorkflowConfig } from 'sprites-types/workflow/core'

interface WorkflowDetailsContentProps {
  workflowConfig: WorkflowConfig
  workflowVersion?: number
}

export function WorkflowDetailsContent({
  workflowConfig,
  workflowVersion,
}: WorkflowDetailsContentProps) {
  const [isParametersExpanded, setIsParametersExpanded] = useState(true)
  const [isOutputsExpanded, setIsOutputsExpanded] = useState(true)
  const [isIntegrationsExpanded, setIsIntegrationsExpanded] = useState(true)

  // Filter out pipedreamAccountId parameters
  const visibleParameters = workflowConfig.parameters?.properties
    ? Object.entries(workflowConfig.parameters.properties).filter(
        ([_paramName, param]) => param.type !== 'pipedreamAccountId'
      )
    : []

  const hasParameters = visibleParameters.length > 0
  const hasOutputs = !!(workflowConfig.outputs && workflowConfig.outputs.length > 0)
  const hasIntegrations = !!(
    workflowConfig.requiredIntegrations && workflowConfig.requiredIntegrations.length > 0
  )

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50/30">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold text-gray-900">{workflowConfig.name}</h1>
          <div className="mt-2 space-y-1">
            {workflowVersion && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Version:</span>
                <span className="font-mono text-sm text-gray-700">{workflowVersion}</span>
              </div>
            )}
            {workflowConfig.id && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Workflow Reference:</span>
                <span className="font-mono text-sm text-gray-700">
                  {WorkflowVersionUtils.create(
                    workflowConfig.id,
                    workflowVersion?.toString() ?? 'latest'
                  )}
                </span>
                <button
                  onClick={() => {
                    const workflowRef = WorkflowVersionUtils.create(
                      workflowConfig.id,
                      workflowVersion?.toString() ?? 'latest'
                    )
                    navigator.clipboard.writeText(workflowRef)
                    toast.success('Copied workflow reference to clipboard')
                  }}
                  className="cursor-pointer rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  title="Copy workflow reference"
                >
                  <Copy size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {workflowConfig.description && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <div className="border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm">
                <h3 className="text-base font-semibold text-gray-900">Description</h3>
              </div>
              <div className="p-6">
                <p className="leading-relaxed text-gray-700">{workflowConfig.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Parameters */}
        {hasParameters && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <button
                onClick={() => setIsParametersExpanded(!isParametersExpanded)}
                className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-base font-semibold text-gray-900">Parameters</h3>
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      {visibleParameters.length} parameter
                      {visibleParameters.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <svg
                    className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                      isParametersExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isParametersExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6">
                  <div className="space-y-4">
                    {visibleParameters.map(([paramName, param]) => {
                      const isRequired =
                        Array.isArray(workflowConfig.parameters.required) &&
                        workflowConfig.parameters.required.includes(paramName)
                      return (
                        <div key={paramName} className="rounded-xl border border-gray-200 p-4">
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-base font-medium text-gray-900">{paramName}</span>
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                              {param.type}
                            </span>
                            {isRequired ? (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                                Required
                              </span>
                            ) : (
                              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
                                Optional
                              </span>
                            )}
                          </div>
                          {param.description && (
                            <p className="mb-2 text-sm text-gray-600">{param.description}</p>
                          )}
                          {param.enum && Array.isArray(param.enum) && param.enum.length > 0 && (
                            <div className="text-sm text-gray-500">
                              <span className="font-medium">Options: </span>
                              {param.enum.join(', ')}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Outputs */}
        {hasOutputs && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <button
                onClick={() => setIsOutputsExpanded(!isOutputsExpanded)}
                className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-base font-semibold text-gray-900">Outputs</h3>
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      {workflowConfig.outputs?.length || 0} output variable
                      {workflowConfig.outputs?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <svg
                    className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                      isOutputsExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isOutputsExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6">
                  <div className="space-y-3">
                    {workflowConfig.outputs?.map((outputVar, index) => (
                      <div key={index} className="rounded-xl border border-gray-200 p-4">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-base font-medium text-gray-900">{outputVar}</span>
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                            Output Variable
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Template variable that will be resolved from workflow execution results
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Required Integrations */}
        {hasIntegrations && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <button
                onClick={() => setIsIntegrationsExpanded(!isIntegrationsExpanded)}
                className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-base font-semibold text-gray-900">Required Integrations</h3>
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      {workflowConfig.requiredIntegrations?.length || 0} integration
                      {workflowConfig.requiredIntegrations?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <svg
                    className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                      isIntegrationsExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isIntegrationsExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {workflowConfig.requiredIntegrations?.map((integration) => {
                      const config = getIntegrationConfig(integration)
                      if (!config) return null
                      const IconComponent = config.icon
                      return (
                        <div
                          key={integration}
                          className="flex items-center gap-3 rounded-xl border border-gray-200 p-4"
                        >
                          <div
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                            style={{
                              backgroundColor: config.shouldColorIcon
                                ? `${config.primaryColor}15`
                                : undefined,
                            }}
                          >
                            <IconComponent
                              className="h-5 w-5"
                              style={
                                config.shouldColorIcon ? { color: config.primaryColor } : undefined
                              }
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {config.displayName}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Node Count */}
        {workflowConfig.nodes && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Total Nodes</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {workflowConfig.nodes.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
